import React, { useCallback, useState, useEffect } from 'react';
import clsx from 'clsx';
import { Button } from '@marriott/mi-ui-library';
import { AIcon } from '../../atoms/AIcon';
import { ASubheader, ASubheaderSize, ASubheaderTag } from '../../atoms/ASubheader';
import { MIconInteractive } from '../MIconInteractive';
import { ALinkStandalone, ALinkStandaloneSize } from '../../atoms/ALinkStandalone';
import { SNACKBAR_MODAL_TYPE } from './MSnackbar.types';
import * as Styled from './MSnackbar.styles';
import type { MSnackbarProps, SnackbarCTA } from './MSnackbar.types';

// Then in the component render:
export const COMPONENT_NAME = 'm-web-ui-kit-MSnackbar';
export const TEST_ID = 'web-ui-kit-MSnackbar';

export const MSnackbar: React.FC<MSnackbarProps> = ({
  modalType,
  successMessage,
  errorMessage,
  ctas: propCtas,
  autoHideDuration: propAutoHideDuration = 5000,
  iconClass: propIconClass,
  className,
  subheaderSize = ASubheaderSize.M,
  subheaderElement = ASubheaderTag.h3,
  closeAriaLabel = 'Close',
  onClose,
}) => {
  const [isOpen, setIsOpen] = useState(true);
  const isSuccess = modalType === SNACKBAR_MODAL_TYPE.SUCCESS;
  const displayMessage = (isSuccess ? successMessage : errorMessage) || '';
  const displayCtas: SnackbarCTA[] = (propCtas ?? []).map(cta => ({
    ...cta,
    isExternalLink: cta.isExternalLink ?? false,
  }));
  const displayIconClass = propIconClass ?? (isSuccess ? 'icon-heart-fill' : 'icon-alert');

  const handleClose = useCallback(() => {
    setIsOpen(false);
    onClose?.();
  }, [onClose]);

  useEffect(() => {
    if (!isOpen || propAutoHideDuration <= 0) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      handleClose();
    }, propAutoHideDuration);

    return () => {
      window.clearTimeout(timer);
    };
  }, [propAutoHideDuration, isOpen, handleClose]);

  return (
    <Styled.Wrapper
      $isOpen={isOpen}
      data-component-name={COMPONENT_NAME}
      data-testid={TEST_ID}
      className={clsx(className, 'msnackbar-bottom-center')}
      $isSuccess={!!isSuccess}
      role={isSuccess ? 'status' : 'alert'}
      aria-label={'MSnackbar'}
    >
      <div className="d-flex align-items-center">
        <div className="d-flex flex-column">
          <div className="d-flex align-items-start gap-2">
            <AIcon icon={clsx('icon-inverse icon-m mb-1', displayIconClass)} />
            <div>
              <ASubheader
                className="t-font-inverse-m m-ellipsis-3lines px-2"
                text={displayMessage}
                size={subheaderSize}
                element={subheaderElement}
              />
              {displayCtas.length > 0 && (
                <div className="msnackbar-links d-flex px-2 pt-2">
                  {displayCtas.map((cta, idx) =>
                    cta.isLink ? (
                      <ALinkStandalone
                        key={idx}
                        href={cta.url}
                        text={cta.label}
                        externalLink={cta.isExternalLink}
                        onClick={cta.callback}
                        className="inverse"
                        size={ALinkStandaloneSize.ExtraSmall}
                      />
                    ) : (
                      <Button key={idx} className="t-font-inverse-xs" callback={cta.callback} buttonCopy={cta.label} />
                    )
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <MIconInteractive aIcon={{ icon: 'icon-inverse icon-clear' }} ariaLabel={closeAriaLabel} onClick={handleClose} />
    </Styled.Wrapper>
  );
};
